import React, { useContext, useState, useEffect } from "react";
import { Card, Button, InlineStack, Layout, Text, BlockStack } from "@shopify/polaris";
import { useMutation, useQuery } from "@apollo/client";
// import helpers
import { baseHelper, errorHelper } from "lib/helpers";

// import context
import { PrivateContext } from "lib/context/privateContext";

import { withErrorBoundary, withFeature } from "lib/hoc";

// import constant
import constant from "lib/constant/constant";

import { Banner, SkeletonAnnotated, Toast } from "lib/components";

// import mutations
import { UPDATE_FULFILLMENT_TRACKING_DETAILS_SETTING } from "app/setup/apollo/mutations";
// import queries
import { GET_FULFILLMENT_TRACKING_DETAILS_SETTING } from "app/setup/apollo/queries";

const OperatorManageTrackingDetails = () => {
  const { gql } = constant;
  const { currentUser, cms } = useContext(PrivateContext);
  const { _id: currentUserId } = currentUser;
  const [message, setMessage] = useState("");
  const [updateAt, setUpdateAt] = useState(new Date());
  const [isEnabledData, setIsEnabledData] = useState(false);
  const [banner, setBanner] = useState({
    title: "",
    status: "",
    isOpen: false,
  });
  const { data: trackingData, loading: fulfillSettingLoading, refetch } = useQuery(
    GET_FULFILLMENT_TRACKING_DETAILS_SETTING
  );

  const [updateFulfillmentTrackingDetailsSetting, { loading }] = useMutation(
    UPDATE_FULFILLMENT_TRACKING_DETAILS_SETTING
  );
  const contentStatus = isEnabledData ? constant.displayStatus.DISABLE : constant.ENABLE;

  useEffect(() => {
    if (trackingData) {
      const resData = baseHelper.getResponseData(trackingData, "getFulfillmentTrackingDetailsSetting");
      const resError = baseHelper.getResponseError(trackingData, "getFulfillmentTrackingDetailsSetting");
      if (resError) {
        setBanner({
          isOpen: true,
          status: constant.CRITICAL,
          title: resError,
        });
        return;
      }
      if (resData) {
        const { isEnabled, updatedAt } = resData || {};
        setIsEnabledData(isEnabled);
        setUpdateAt(updatedAt);
      }
    }
  }, [trackingData, setBanner, "getFulfillmentTrackingDetailsSetting"]);

  const onCodeSubmit = async (enable) => {
    const formData = {
      isEnabled: enable,
      sellerId: currentUserId,
    };
    try {
      const response = await updateFulfillmentTrackingDetailsSetting({
        variables: {
          input: formData,
        },
      });

      const responseError = baseHelper.getResponseError(response.data, "updateFulfillmentTrackingDetailsSetting");

      if (responseError) {
        setBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
        return;
      }

      if (!enable) {
        setMessage(cms("message.success.disable"));
        setIsEnabledData(!isEnabledData);
      } else {
        setMessage(cms("message.success.enable"));
      }
      refetch();
    } catch (exception) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
    }
  };

  const renderBanner = () => {
    const { status, title, isOpen } = banner;
    return (
      <Banner
        isOpen={isOpen}
        status={status}
        title={title}
        onDismiss={() => setBanner({ isOpen: false, status: "", title: "" })}
      />
    );
  };
  if (fulfillSettingLoading) {
    return <SkeletonAnnotated />;
  }
  return (
    <>
      {banner.isOpen && <Layout.Section>{renderBanner()}</Layout.Section>}
      <Layout.AnnotatedSection
        title={cms("label.section.setting.title")}
        description={cms("label.section.setting.description")}
      >
        <Card>
          <InlineStack wrap={false} blockAlign="start" gap="400">
            <BlockStack>
              <Text as="h6" variant="headingMd">
                {cms("label.section.editFulfillment.title")}
              </Text>
              <Text as="span" tone="subdued">
                <Text variant="bodySm" as="p">{`${baseHelper.lastUpdateDate(updateAt)}`}</Text>
              </Text>
              <br />
              <Text as="span">{cms("label.section.editFulfillment.description")}</Text>
            </BlockStack>
            <Button
              id="auto"
              variant={!isEnabledData ? "primary" : "primary"}
              onClick={() => onCodeSubmit(!isEnabledData)}
              loading={loading}
              buttonType={contentStatus}
              tone={isEnabledData ? "critical" : ""}
              size="Large"
            >
              {isEnabledData ? "Disable" : "Enable"}
            </Button>
          </InlineStack>
        </Card>
        <div className="toast">
          <Toast message={message} setToast={setMessage} />
        </div>
      </Layout.AnnotatedSection>
    </>
  );
};

export default withFeature(withErrorBoundary(OperatorManageTrackingDetails), {
  feature: constant.MANAGE_FULFILLMENT_DETAIL,
});
